import { CANVAS_PRESETS, type StudioDocument } from '../store/studioStore';
import { TEXT_TECHNIQUE_ID, createLayer, type Layer } from '../store/layer';
import { coercePalette, type PaletteSpec } from '../core/palette';
import { defaultValues, type ParamValue } from '../core/params/types';
import { randomizeValues } from '../core/params/randomize';
import { createRng, type Rng } from '../core/rng';
import { getTechnique } from '../techniques/registry';
import { applyDimension, collectDimensions, readDimension, valueFromUnit, type Dimension } from './dimensions';
import type { BatchConfig, Variant, VariantMeta } from './types';

/**
 * Turns a configuration into a concrete list of documents to render.
 *
 * Planning is separated from rendering on purpose: the plan is deterministic and cheap,
 * so the UI can show exactly how many variants a configuration produces — and which
 * axes it sweeps — before committing to minutes of work.
 */
export interface PlannedVariant {
  id: string;
  doc: StudioDocument;
  meta: VariantMeta;
}

const cloneDoc = (doc: StudioDocument): StudioDocument => structuredClone(doc);

function pickTechniques(config: BatchConfig, rng: Rng): string[] {
  const available = config.techniqueIds.filter((id) => getTechnique(id));
  if (available.length === 0) return [];
  if (!config.combineLayers) return available;

  const min = Math.max(1, Math.min(config.minLayers, config.maxLayers));
  const max = Math.max(min, config.maxLayers);
  const count = Math.min(available.length, rng.int(min, max));

  const weights = available.map((id) => Math.max(0.0001, config.techniqueWeights[id] ?? 1));
  const chosen: string[] = [];
  const pool = [...available];
  const poolWeights = [...weights];
  for (let i = 0; i < count && pool.length; i++) {
    const pick = rng.pickWeighted(pool, poolWeights);
    const index = pool.indexOf(pick);
    pool.splice(index, 1);
    poolWeights.splice(index, 1);
    chosen.push(pick);
  }
  return chosen;
}

/** Replaces the technique layers of a document with a freshly drawn combination. */
function rebuildLayers(doc: StudioDocument, techniqueIds: string[], rng: Rng): void {
  const textLayer = doc.layers.find((l) => l.techniqueId === TEXT_TECHNIQUE_ID);
  const layers: Layer[] = textLayer ? [textLayer] : [];
  for (const id of techniqueIds) {
    const technique = getTechnique(id);
    if (!technique) continue;
    layers.push(
      createLayer(id, technique.name, technique.params, {
        seed: rng.int(0, 999999999),
        values: defaultValues(technique.params),
      }),
    );
  }
  doc.layers = layers;
  doc.activeLayerId = layers[layers.length - 1]?.id ?? doc.activeLayerId;
}

function applyPalette(doc: StudioDocument, paletteId: string): void {
  for (const layer of doc.layers) {
    for (const [key, value] of Object.entries(layer.values)) {
      if (value && typeof value === 'object' && 'source' in (value as PaletteSpec)) {
        layer.values[key] = coercePalette({ ...(value as PaletteSpec), source: 'curated', paletteId });
      }
    }
  }
}

function collectPaletteName(doc: StudioDocument): string {
  for (const layer of doc.layers) {
    for (const value of Object.values(layer.values)) {
      if (value && typeof value === 'object' && 'source' in (value as PaletteSpec)) {
        const spec = value as PaletteSpec;
        return spec.source === 'curated' ? spec.paletteId : spec.source;
      }
    }
  }
  return '—';
}

function describe(doc: StudioDocument, index: number, dims: Dimension[]): VariantMeta {
  const dimensions: Record<string, string | number | boolean> = {};
  for (const dim of dims) {
    const value = readDimension(doc, dim);
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      dimensions[dim.label] = value;
    }
  }
  return {
    index,
    seed: doc.seed,
    word: String(doc.textValues.text ?? '').split('\n')[0] || '—',
    fontId: doc.fontId,
    techniques: doc.layers.filter((l) => l.techniqueId !== TEXT_TECHNIQUE_ID).map((l) => l.techniqueId),
    palette: collectPaletteName(doc),
    canvas: `${doc.canvas.width}×${doc.canvas.height}`,
    dimensions,
  };
}

/** Applies the non-parameter dimensions: words, fonts, formats, palettes, backgrounds, post-FX. */
function applyGlobals(doc: StudioDocument, config: BatchConfig, rng: Rng, seed: number): void {
  doc.seed = seed;

  if (config.varyText && config.words.length) {
    doc.textValues.text = rng.pick(config.words);
  }
  if (config.varyFonts && config.fontIds.length) {
    doc.fontId = rng.pick(config.fontIds);
    // Axis values belong to the previous font; drop them so the new one starts clean.
    for (const key of Object.keys(doc.textValues)) {
      if (key.startsWith('axis:')) delete doc.textValues[key];
    }
  }
  if (config.varyCanvas && config.canvasPresetIds.length) {
    const preset = CANVAS_PRESETS.find((p) => p.id === rng.pick(config.canvasPresetIds));
    if (preset) {
      doc.canvas = { ...doc.canvas, width: preset.width, height: preset.height };
    }
  }
  if (config.varyPalettes && config.paletteIds.length) {
    applyPalette(doc, rng.pick(config.paletteIds));
  }
  if (config.varyBackground && config.backgroundKinds.length) {
    doc.background.kind = rng.pick(config.backgroundKinds);
  }

  for (const [effectId, mode] of Object.entries(config.postMode)) {
    const entry = doc.postFx[effectId];
    if (!entry) continue;
    if (mode === 'on') entry.enabled = true;
    else if (mode === 'off') entry.enabled = false;
    else entry.enabled = rng.chance(config.postProbability);
  }
}

export interface Plan {
  variants: PlannedVariant[];
  dimensions: Dimension[];
  /** Human-readable note about what the strategy did, shown above the gallery. */
  note: string;
}

export function planBatch(
  config: BatchConfig,
  baseDoc: StudioDocument,
  planSeed: number,
  options: { mutationBase?: StudioDocument; morphTarget?: StudioDocument } = {},
): Plan {
  const rng = createRng(planSeed);
  const variants: PlannedVariant[] = [];

  const sourceDoc = config.strategy === 'mutation' ? (options.mutationBase ?? baseDoc) : baseDoc;
  const dims = collectDimensions(sourceDoc, {
    layerIds: new Set(config.includeLayerParams ? sourceDoc.layers.map((l) => l.id) : []),
    includeText: config.includeTextParams,
    includeBackground: config.includeBackgroundParams,
    includePost: config.includePostParams,
  });

  const seedFor = (i: number, total: number): number => {
    if (config.strategy === 'seedSweep') {
      const span = Math.max(0, config.seedTo - config.seedFrom);
      return total <= 1 ? config.seedFrom : Math.round(config.seedFrom + (span * i) / (total - 1));
    }
    return rng.int(0, 999999999);
  };

  const make = (i: number, total: number, fill: (doc: StudioDocument, dims: Dimension[]) => void): void => {
    const doc = cloneDoc(sourceDoc);
    if (config.combineLayers && config.strategy !== 'morph') {
      rebuildLayers(doc, pickTechniques(config, rng), rng);
    }
    applyGlobals(doc, config, rng, seedFor(i, total));

    // Layer ids change when the stack is rebuilt, so dimensions are re-collected
    // against the document that is actually being filled.
    const localDims = config.combineLayers
      ? collectDimensions(doc, {
          layerIds: new Set(config.includeLayerParams ? doc.layers.map((l) => l.id) : []),
          includeText: config.includeTextParams,
          includeBackground: config.includeBackgroundParams,
          includePost: config.includePostParams,
        })
      : dims;

    fill(doc, localDims);
    variants.push({ id: `v${i}`, doc, meta: describe(doc, i, localDims) });
  };

  switch (config.strategy) {
    case 'grid': {
      const axes = config.gridAxes
        .map((axis) => ({ axis, dim: dims.find((d) => d.id === axis.dimensionId) }))
        .filter((a): a is { axis: typeof a.axis; dim: Dimension } => Boolean(a.dim))
        .slice(0, 3);

      if (axes.length === 0) {
        // No axes chosen: a grid over nothing is a seed sweep, which is at least useful.
        for (let i = 0; i < config.count; i++) make(i, config.count, () => undefined);
        break;
      }

      const total = axes.reduce((n, a) => n * Math.max(2, a.axis.steps), 1);
      for (let i = 0; i < total; i++) {
        let rest = i;
        const coords = axes.map((a) => {
          const steps = Math.max(2, a.axis.steps);
          const c = rest % steps;
          rest = Math.floor(rest / steps);
          return c / (steps - 1);
        });
        make(i, total, (doc, localDims) => {
          axes.forEach((a, k) => {
            const dim = localDims.find((d) => d.id === a.dim.id) ?? a.dim;
            applyDimension(doc, dim, valueFromUnit(dim, coords[k]));
          });
        });
      }
      break;
    }

    case 'hypercube': {
      // Latin hypercube: one sample per stratum per dimension, then each dimension's
      // strata are shuffled independently. Every dimension is therefore covered evenly,
      // which plain random sampling only manages on average.
      const n = config.count;
      const columns = dims.map(() => {
        const strata = Array.from({ length: n }, (_, k) => (k + rng.next()) / n);
        return rng.shuffle(strata);
      });
      for (let i = 0; i < n; i++) {
        make(i, n, (doc, localDims) => {
          dims.forEach((dim, k) => {
            const target = localDims.find((d) => d.id === dim.id) ?? dim;
            applyDimension(doc, target, valueFromUnit(target, columns[k][i]));
          });
        });
      }
      break;
    }

    case 'seedSweep':
      for (let i = 0; i < config.count; i++) make(i, config.count, () => undefined);
      break;

    case 'mutation': {
      const strength = Math.max(0, Math.min(1, config.mutationStrength));
      // Below 25 % nudge the neighbourhood, above 60 % redraw freely; in between move
      // substantially but still relative to the base.
      const level = strength < 0.25 ? 'gentle' : strength < 0.6 ? 'medium' : 'wild';
      for (let i = 0; i < config.count; i++) {
        make(i, config.count, (doc) => {
          for (const layer of doc.layers) {
            const technique = getTechnique(layer.techniqueId);
            if (!technique) continue;
            layer.values = randomizeValues(technique.params, layer.values, layer.bulk, rng, level);
          }
        });
      }
      break;
    }

    case 'morph': {
      const target = options.morphTarget;
      const n = Math.max(2, config.count);
      for (let i = 0; i < n; i++) {
        const t = i / (n - 1);
        make(i, n, (doc, localDims) => {
          if (!target) return;
          for (const dim of localDims) {
            const from = readDimension(baseDoc, dim);
            const to = readDimension(target, dim);
            if (typeof from === 'number' && typeof to === 'number') {
              const mixed = from + (to - from) * t;
              applyDimension(doc, dim, dim.param.kind === 'int' ? Math.round(mixed) : mixed);
            } else if (to !== undefined && t >= 0.5) {
              // Discrete values cannot be interpolated; they switch at the midpoint.
              applyDimension(doc, dim, to as ParamValue);
            }
          }
        });
      }
      break;
    }

    default:
      for (let i = 0; i < config.count; i++) {
        make(i, config.count, (doc, localDims) => {
          for (const dim of localDims) applyDimension(doc, dim, valueFromUnit(dim, rng.next()));
        });
      }
  }

  return { variants, dimensions: dims, note: describePlan(config, dims, variants.length) };
}

function describePlan(config: BatchConfig, dims: Dimension[], produced: number): string {
  const parts = [`${produced} Varianten`];
  if (config.strategy === 'grid') {
    const axes = config.gridAxes.filter((a) => dims.some((d) => d.id === a.dimensionId));
    if (axes.length) {
      parts.push(
        axes
          .map((a) => `${dims.find((d) => d.id === a.dimensionId)?.label} × ${Math.max(2, a.steps)}`)
          .join(' · '),
      );
    }
  } else if (dims.length) {
    parts.push(`${dims.length} variable Parameter`);
  }
  if (config.combineLayers) parts.push(`${config.minLayers}–${config.maxLayers} Layer je Bild`);
  return parts.join(' — ');
}

export function createVariant(planned: PlannedVariant): Variant {
  return {
    id: planned.id,
    meta: planned.meta,
    doc: planned.doc,
    blob: null,
    url: null,
    width: 0,
    height: 0,
    ms: 0,
    elements: 0,
    favourite: false,
    hidden: false,
    error: null,
  };
}
